import _ from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import useBreakpoints from '../../../../common/hooks/useBreakpoints';
import Breadcrumbs from '../../../../common/widgets/Breadcrumbs';
import Button from '../../../../common/widgets/Button';
import Dialog from '../../../../common/widgets/Dialog';
import Icons from '../../../../common/widgets/Icons';
import { BenefitsPageProps } from '../../contexts/common';
import { CustomerInfoContext } from '../../contexts/CustomerInfoContext';
import { RevolutionSliderTypes, TopLevelCategories } from '../../enums';
import { Category } from '../../models/Categories';
import { TaxFilter } from '../../models/Filters';
import { resources } from '../../resources/resources';
import { SmartNotifications } from '../../widgets/SmartNotifications';
import applicationCrumbs from '../ApplicationCrumbs';
import { updateArrayQueryParam } from '../CategoryPage/utils';
import CategoriesSideMenu from '../Common/CategoriesSideMenu';
import TaxationLevelFilter from '../Common/Filters/TaxationLevelFilter';
import MktSlideshow from '../Common/Marketing/MktSlideshow';
import BenefitsPanel from './BenefitsPanel';
import { queryParamKeys } from './constants';
import {
  clearFilterQueryParams,
  getInitialTaxClassIdsFromQueryParams,
} from './utils';

export default function BenefitsPage(props: Readonly<BenefitsPageProps>) {
  const { rootCategory, taxList, employerTaxList, topLevelCategoryId } = props;
  const { t: translate } = useTranslation();
  const { customerInfo } = useContext(CustomerInfoContext);
  const { isTablet, isOnlyTablet } = useBreakpoints();
  const [searchParams] = useSearchParams();

  const crumbs = applicationCrumbs(translate);

  const getCrumbForCategory = (categoryId: number) => {
    switch (categoryId) {
      case TopLevelCategories.FlexibleBenefits:
        return crumbs.flexibleBenefits;
      case TopLevelCategories.Cashback:
        return crumbs.cashback;
      case TopLevelCategories.BenefitClub:
        return crumbs.clubBenefits;
      case TopLevelCategories.DiscountVouchers:
        return crumbs.discountVouchers;
      default:
        return crumbs.clubBenefits;
    }
  };

  const [filtredCategories, setFiltredCategories] = useState<
    Category[] | undefined
  >(rootCategory?.children);
  const [title, setTitle] = useState<string>();
  const [showFilterDialog, setShowFilterDialog] = useState<boolean>(false);
  const [selectedEmployeeTaxClassIds, setSelectedEmployeeTaxClassIds] =
    useState<number[]>(getInitialTaxClassIdsFromQueryParams(searchParams));
  const [selectedEmployerTaxClassIds, setSelectedEmployerTaxClassIds] =
    useState<number[]>(getInitialTaxClassIdsFromQueryParams(searchParams));
  const [filteredEmployeeTaxList, setFilteredEmployeeTaxList] = useState<
    TaxFilter[]
  >(taxList || []);
  const [filteredEmployerTaxList, setFilteredEmployerTaxList] = useState<
    TaxFilter[]
  >(employerTaxList || []);

  const setFiltersAfterSelection = (
    fullFilteredCategories: Category[],
    selectedTaxClassIds: number[],
    changedFilterType: 'employee' | 'employer',
  ) => {
    const displayedEmployerTaxClassIds = new Set(
      fullFilteredCategories
        .flatMap(c => c.employerTaxClassIds)
        .concat(
          changedFilterType === 'employer'
            ? selectedTaxClassIds
            : selectedEmployerTaxClassIds,
        ),
    );

    const displayedEmployeeTaxClassIds = new Set(
      fullFilteredCategories
        .flatMap(c => c.taxClassIds)
        .concat(
          changedFilterType === 'employee'
            ? selectedTaxClassIds
            : selectedEmployeeTaxClassIds,
        ),
    );

    setFilteredEmployerTaxList(
      (employerTaxList ?? []).filter(tx =>
        displayedEmployerTaxClassIds.has(tx.taxClassId),
      ),
    );

    setFilteredEmployeeTaxList(
      (taxList ?? []).filter(tx =>
        displayedEmployeeTaxClassIds.has(tx.taxClassId),
      ),
    );
  };

  const handleTaxFilterChanged = (selectedTaxClassIds: number[]) => {
    setSelectedEmployeeTaxClassIds(selectedTaxClassIds);
    const allSelectedTaxClassIds = _.union(
      selectedTaxClassIds,
      selectedEmployerTaxClassIds,
    );
    updateArrayQueryParam(queryParamKeys.taxClassIds, allSelectedTaxClassIds);

    const partialFilteredCategories =
      selectedTaxClassIds.length > 0
        ? _.filter(
            rootCategory?.children ?? [],
            c => _.some(selectedTaxClassIds, id => c.taxClassIds.includes(id)), // CEL PUTIN UNA
          )
        : rootCategory?.children ?? [];
    const fullFilteredCategories =
      selectedEmployerTaxClassIds.length > 0
        ? _.filter(
            partialFilteredCategories,
            c =>
              _.some(selectedEmployerTaxClassIds, id =>
                c.employerTaxClassIds.includes(id),
              ), // CEL PUTIN UNA
          )
        : partialFilteredCategories ?? [];

    setFiltersAfterSelection(
      fullFilteredCategories,
      selectedTaxClassIds,
      'employee',
    );

    setFiltredCategories(fullFilteredCategories);
  };

  const handleEmployerTaxFilterChanged = (selectedTaxClassIds: number[]) => {
    setSelectedEmployerTaxClassIds(selectedTaxClassIds);
    const allSelectedTaxClassIds = _.union(
      selectedTaxClassIds,
      selectedEmployeeTaxClassIds,
    );

    updateArrayQueryParam(queryParamKeys.taxClassIds, allSelectedTaxClassIds);

    const partialFilteredCategories =
      selectedTaxClassIds.length > 0
        ? _.filter(
            rootCategory?.children ?? [],
            c =>
              _.some(selectedTaxClassIds, id =>
                c.employerTaxClassIds.includes(id),
              ), // CEL PUTIN UNA
          )
        : rootCategory?.children ?? [];
    const fullFilteredCategories =
      selectedEmployeeTaxClassIds.length > 0
        ? _.filter(
            partialFilteredCategories,
            c =>
              _.some(selectedEmployeeTaxClassIds, id =>
                c.taxClassIds.includes(id),
              ), // CEL PUTIN UNA
          )
        : partialFilteredCategories ?? [];

    setFiltersAfterSelection(
      fullFilteredCategories,
      selectedTaxClassIds,
      'employer',
    );

    setFiltredCategories(fullFilteredCategories);
  };

  const handleClearFilters = () => {
    clearFilterQueryParams();
    setSelectedEmployeeTaxClassIds([]);
    setSelectedEmployerTaxClassIds([]);
    setFilteredEmployeeTaxList(taxList || []);
    setFilteredEmployerTaxList(employerTaxList || []);
    setFiltredCategories(() => rootCategory?.children);
  };

  const showTaxFilter = () => {
    if (customerInfo.isGrossUpUser) return false;

    return (
      new Set(filteredEmployeeTaxList.map((tx: TaxFilter) => tx.taxClassId))
        .size > 1 || selectedEmployeeTaxClassIds.length > 0
    );
  };

  const showEmploayerTaxFilter = () => {
    if (customerInfo.isGrossUpUser) return false;

    return (
      new Set(filteredEmployerTaxList.map((tx: TaxFilter) => tx.taxClassId))
        .size > 1 || selectedEmployerTaxClassIds.length > 0
    );
  };

  const hasFilters = () => {
    return showTaxFilter() || showEmploayerTaxFilter();
  };

  const hasSelectedFilters = () => {
    return (
      selectedEmployeeTaxClassIds.length > 0 ||
      selectedEmployerTaxClassIds.length > 0
    );
  };

  useEffect(() => {
    setFiltredCategories(rootCategory?.children);

    let title;
    if (topLevelCategoryId === TopLevelCategories.FlexibleBenefits) {
      if (customerInfo.isGrossUpUser) {
        title = translate(resources.grossUp.flexibleBenefits.title);
      } else {
        title = translate(resources.menu.flexibleBenefits);
      }
    } else if (topLevelCategoryId === TopLevelCategories.Cashback) {
      title = translate(resources.menu.cashback);
    } else if (topLevelCategoryId === TopLevelCategories.DiscountVouchers) {
      title = translate(resources.menu.discountVouchers);
    } else {
      title = translate(resources.menu.benefitClub);
    }
    setTitle(title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootCategory, topLevelCategoryId]);

  useEffect(() => {
    if (taxList) {
      setFilteredEmployeeTaxList(taxList);
    }
    if (employerTaxList) {
      setFilteredEmployerTaxList(employerTaxList);
    }
  }, [employerTaxList, taxList]);

  return (
    <>
      <Breadcrumbs
        entries={[crumbs.home, getCrumbForCategory(topLevelCategoryId)]}
      />
      {!isTablet ? (
        <section className="section-main section-actions flexible-benefits-page">
          <div className="container">
            <div className="area-actions">
              {rootCategory && rootCategory?.children.length > 0 && (
                <CategoriesSideMenu rootCategory={rootCategory} />
              )}
              {hasSelectedFilters() && (
                <button
                  type="button"
                  className="button-panel"
                  onClick={handleClearFilters}
                >
                  <span>{translate(resources.searchPage.clearFilters)}</span>
                  <span className="button-panel-icon">
                    <Icons.ClearFilters />
                  </span>
                </button>
              )}
              {showTaxFilter() && (
                <TaxationLevelFilter
                  taxationLevelList={filteredEmployeeTaxList}
                  handleTaxFilterChanged={handleTaxFilterChanged}
                  prevSelectedTaxClassIds={selectedEmployeeTaxClassIds}
                  title={translate(resources.filters.taxationLevelTitle)}
                />
              )}
              {showEmploayerTaxFilter() && (
                <TaxationLevelFilter
                  taxationLevelList={filteredEmployerTaxList}
                  handleTaxFilterChanged={handleEmployerTaxFilterChanged}
                  prevSelectedTaxClassIds={selectedEmployerTaxClassIds}
                  title={translate(
                    resources.filters.employerTaxationLevelTitle,
                  )}
                />
              )}
            </div>
            <div className="area-content">
              {topLevelCategoryId === TopLevelCategories.FlexibleBenefits && (
                <MktSlideshow
                  sliderType={RevolutionSliderTypes.FlexibleBenefitsSlideshow}
                />
              )}
              {(topLevelCategoryId === TopLevelCategories.BenefitClub ||
                topLevelCategoryId === TopLevelCategories.Cashback) && (
                <MktSlideshow
                  sliderType={RevolutionSliderTypes.BenefitClubSlideshow}
                />
              )}
              <BenefitsPanel
                topLevelCategoryId={topLevelCategoryId}
                title={title}
                categories={filtredCategories}
              />
              <div className="toast-wrapper-bottom">
                <div>
                  <SmartNotifications
                    productId={null}
                    categoryId={Number(topLevelCategoryId)}
                    isForHome={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="section-main section-full mobile-page flexible-benefits-page">
          <div className="container">
            <div className="product-list-header-mb">
              {isOnlyTablet && (
                <>
                  {topLevelCategoryId ===
                    TopLevelCategories.FlexibleBenefits && (
                    <MktSlideshow
                      sliderType={
                        RevolutionSliderTypes.FlexibleBenefitsSlideshow
                      }
                    />
                  )}
                  {(topLevelCategoryId === TopLevelCategories.BenefitClub ||
                    topLevelCategoryId === TopLevelCategories.Cashback) && (
                    <MktSlideshow
                      sliderType={RevolutionSliderTypes.BenefitClubSlideshow}
                    />
                  )}
                </>
              )}
              <h1 className="category-title">{title}</h1>
              <div className="mobile-filters-area">
                {hasFilters() && (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setShowFilterDialog(true)}
                  >
                    {translate(resources.searchPage.filter)}
                    <span className="btn-icon">
                      <Icons.ArrowDownMobileFilter />
                    </span>
                  </button>
                )}
              </div>
              {showFilterDialog && hasFilters() && (
                <Dialog
                  closeDialog={() => setShowFilterDialog(false)}
                  showCloseIcon
                  headerContent={
                    <div className="dialog-title">
                      {translate(resources.searchPage.filter)}
                    </div>
                  }
                  className="dialog-m mobile-filter-dialog"
                >
                  <div className="dialog-content filter-content mobile-forms">
                    {showTaxFilter() && (
                      <TaxationLevelFilter
                        taxationLevelList={taxList}
                        handleTaxFilterChanged={handleTaxFilterChanged}
                        prevSelectedTaxClassIds={selectedEmployeeTaxClassIds}
                        title={translate(resources.filters.taxationLevelTitle)}
                      />
                    )}
                  </div>
                  <div className="dialog-content filter-content mobile-forms">
                    {showEmploayerTaxFilter() && (
                      <TaxationLevelFilter
                        taxationLevelList={employerTaxList}
                        handleTaxFilterChanged={handleEmployerTaxFilterChanged}
                        prevSelectedTaxClassIds={selectedEmployerTaxClassIds}
                        title={translate(
                          resources.filters.employerTaxationLevelTitle,
                        )}
                      />
                    )}
                  </div>
                  <div className="dialog-actions">
                    <Button
                      className="btn btn-outline"
                      disabled={!selectedEmployeeTaxClassIds.length}
                      onClick={() => {
                        handleClearFilters();
                        setShowFilterDialog(false);
                      }}
                    >
                      {translate(resources.searchPage.cancel)}
                    </Button>
                    <Button
                      className="btn btn-solid"
                      onClick={() => setShowFilterDialog(false)}
                    >
                      {translate(resources.searchPage.checkProducts)}
                    </Button>
                  </div>
                </Dialog>
              )}
            </div>
            <div className="area-content products-content-mb">
              <BenefitsPanel
                topLevelCategoryId={topLevelCategoryId}
                title={title}
                categories={filtredCategories}
              />
            </div>
          </div>
        </section>
      )}
    </>
  );
}
